import React, { useMemo } from 'react';
import _ from 'lodash';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAppSelector } from '../../../../../hooks/hooks';
import TiminatorTableSettings, { TiminatorTableColumn } from '../../../../../components/shared/table-v2/types';
import { AbsenceBehavior, TiminatorAbsenceType } from '../../../../../features/types/types';
import { Lx } from '../../../../../i18n/consts';
import { StatusTag } from '../../../../../components/shared/status-tag';
import { DeleteIconButton } from '../../../../../components/shared/delete-icon-button';
import { StyledMobileDefaultText } from '../../../../../components/shared/styled-mobile-default-text';
import { normalizeString } from '../../../../../utils/strings';
import { buildSearchValues, filterByStatusAndSearch, getStatusSortValue, getStatusValue } from '../../../../../utils/table-filters';

export const useAbsenceTypesTable = (
    search: string,
    selectedStatus: number,
    isMobile: boolean
): TiminatorTableSettings<TiminatorAbsenceType> => {
    const intl = useIntl();
    const absenceTypes = useAppSelector(store => store.types.absences.items);

    const getBehaviorLabel = (behavior: AbsenceBehavior): string => {
        switch (behavior) {
            case AbsenceBehavior.Vacation:
                return intl.formatMessage({ id: Lx.General.USE_FOR_VACATION });
            case AbsenceBehavior.WillWork:
                return intl.formatMessage({ id: Lx.General.USE_FOR_WILL_WORK });
            case AbsenceBehavior.LimitToPlannedHours:
                return intl.formatMessage({ id: Lx.General.LIMIT_TO_PLANNED_HOURS });
            default:
                return intl.formatMessage({ id: Lx.General.NONE });
        }
    };
    const hasBehavior = (behavior: AbsenceBehavior): boolean => (
        behavior === AbsenceBehavior.Vacation
        || behavior === AbsenceBehavior.WillWork
        || behavior === AbsenceBehavior.LimitToPlannedHours
    );

    const columns = useMemo((): TiminatorTableColumn<TiminatorAbsenceType>[] => {
        const baseColumns: TiminatorTableColumn<TiminatorAbsenceType>[] = [];
        const getTypeSortValue = (rowData: TiminatorAbsenceType) => {
            if (rowData.default) {
                return 2;
            }
            if (hasBehavior(rowData.behavior)) {
                return 1;
            }
            return 0;
        };

        if (isMobile) {
            baseColumns.push({
                id: 'name',
                label: intl.formatMessage({ id: Lx.General.NAME }),
                sortable: true,
                getSortValue: (rowData: TiminatorAbsenceType) => normalizeString(rowData.name),
                format: (rowData: TiminatorAbsenceType) => {
                    return (
                        <span>
                            <div>{rowData.name}</div>
                            {rowData.default && (
                                <StyledMobileDefaultText text={intl.formatMessage({ id: Lx.General.DEFAULT })} />
                            )}

                            {hasBehavior(rowData.behavior) && (
                                <StyledMobileDefaultText text={getBehaviorLabel(rowData.behavior)} />
                            )}
                        </span>
                    );
                },
            });
        } else {
            baseColumns.push(
                {
                    id: 'name',
                    label: intl.formatMessage({ id: Lx.General.NAME }),
                    sortable: true,
                    getSortValue: (rowData: TiminatorAbsenceType) => normalizeString(rowData.name),
                    format: (rowData: TiminatorAbsenceType) => rowData.name,
                },
                {
                    id: 'default',
                    label: intl.formatMessage({ id: Lx.General.TYPE }),
                    sortable: true,
                    getSortValue: getTypeSortValue,
                    format: (rowData: TiminatorAbsenceType) => {
                        return (
                            <span>
                                {rowData.default && <div>{intl.formatMessage({ id: Lx.General.DEFAULT })}</div>}
                                {hasBehavior(rowData.behavior) && <div>{getBehaviorLabel(rowData.behavior)}</div>}
                            </span>
                        );
                    },
                }
            );
        }

        baseColumns.push(
            {
                id: 'status',
                label: 'Status',
                align: isMobile ? 'right' : 'left',
                sortable: true,
                getSortValue: (rowData: TiminatorAbsenceType) => getStatusSortValue(rowData.active),
                format: (rowData: TiminatorAbsenceType) => {
                    return (
                        <StatusTag status={rowData.active ? 'active' : 'suspended'}>
                            <span>
                                {rowData.active ? <FormattedMessage id="active" /> : <FormattedMessage id="suspended" />}
                            </span>
                        </StatusTag>
                    );
                },
                width: isMobile ? '30%' : undefined,
            },
            {
                id: 'deleteButton',
                format: (rowData: TiminatorAbsenceType) => {
                    return <DeleteIconButton />;
                },
                width: isMobile ? '24px' : '64px',
            }
        )

        return baseColumns;
    }, [isMobile, intl]);

    const data = useMemo(() => (
        filterByStatusAndSearch(
            absenceTypes,
            selectedStatus,
            absenceType => absenceType.active,
            search,
            absenceType => buildSearchValues([
                absenceType.name,
                absenceType.default ? intl.formatMessage({ id: Lx.General.DEFAULT }) : '',
                getBehaviorLabel(absenceType.behavior),
                getStatusValue(intl, absenceType.active),
            ])
        )
    ), [absenceTypes, search, selectedStatus]);

    const isEmpty = absenceTypes.length === 0;

    return {
        columns,
        data,
        isEmpty,
    };
};
